// angular import
import { NgIf } from '@angular/common';
import { Component, inject, input, OnInit, OnDestroy, output } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';

// project import

// icon
import { IconService, IconDirective } from '@ant-design/icons-angular';
import {
	BellOutline,
	SettingOutline,
	GiftOutline,
	MessageOutline,
	PhoneOutline,
	CheckCircleOutline,
	LogoutOutline,
	EditOutline,
	UserOutline,
	ProfileOutline,
	WalletOutline,
	QuestionCircleOutline,
	LockOutline,
	CommentOutline,
	UnorderedListOutline,
	ArrowRightOutline,
	GithubOutline,
	WarningOutline,
	CloseCircleOutline
} from '@ant-design/icons-angular/icons';
import { NgbDropdownModule, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { UsersService } from '@shared/data-access/users.service';
import { ProfileMeResponseDto } from '@shared/interfaces/profile-me.interface';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { UserNotificationsService } from '@shared/data-access/user-notifications.service';
import { IUserNotification } from '@shared/interfaces/user-notifications.interface';
import { WebSocketService } from '@shared/data-access/websocket.service';
import { ServicesStore } from 'src/app/services/storage/services_storage';

@Component({
	selector: 'app-nav-right',
	imports: [IconDirective, RouterModule, NgScrollbarModule, NgbNavModule, NgbDropdownModule, NgIf],
	templateUrl: './nav-right.component.html',
	styleUrls: ['./nav-right.component.scss']
})
export class NavRightComponent implements OnInit, OnDestroy {
	getProfile: ProfileMeResponseDto | null = null;
	notifications: IUserNotification[] = [];
	unreadCount: number = 0;
	isLoadingNotifications: boolean = false;

	private iconService = inject(IconService);
	private usersService = inject(UsersService);
	private notificationsService = inject(UserNotificationsService);
	private webSocketService = inject(WebSocketService);
	private servicesStore = inject(ServicesStore);

	private notificationSubscription?: Subscription;
	private alertSubscription?: Subscription;
	private messageSubscription?: Subscription;
	private notificationStateSub?: Subscription;

	styleSelectorToggle = input<boolean>();
	Customize = output();
	windowWidth: number;
	screenFull: boolean = true;

	constructor(private router: Router) {
		this.windowWidth = window.innerWidth;
		this.iconService.addIcon(
			...[
				CheckCircleOutline,
				GiftOutline,
				MessageOutline,
				SettingOutline,
				PhoneOutline,
				LogoutOutline,
				UserOutline,
				EditOutline,
				ProfileOutline,
				QuestionCircleOutline,
				LockOutline,
				CommentOutline,
				UnorderedListOutline,
				ArrowRightOutline,
				BellOutline,
				GithubOutline,
				WalletOutline,
				WarningOutline,
				CloseCircleOutline
			]
		);
	}
	async ngOnInit(): Promise<void> {
		try {
			this.getUserInfo();
			this.loadNotifications();
			this.notificationStateSub = this.notificationsService.notificationRead$.subscribe(() => {
				this.loadNotifications();
			});
			this.initializeWebSocket();
		} catch (error) {
			console.error('Error al cargar el perfil:', error);
		}
	}

	ngOnDestroy(): void {
		// Limpiar suscripciones de WebSocket
		this.notificationSubscription?.unsubscribe();
		this.alertSubscription?.unsubscribe();
		this.messageSubscription?.unsubscribe();
		this.notificationStateSub?.unsubscribe();
	}

	onLogout(): void {
		this.servicesStore.resetState();
		localStorage.removeItem('token');
		localStorage.removeItem('userInfo');
		this.router.navigate(['/login']);
	}

	async getUserInfo(): Promise<void> {
		this.usersService.getUserInfo().subscribe((data) => {
			const firstName = data.result.name.split(' ')[0];
			const firstLastName = data.result.lastName.split(' ')[0];
			this.getProfile = {
				id: data.result.id,
				firstName,
				firstLastName,
				name: data.result.name,
				lastName: data.result.lastName,
				username: data.result.email,
				email: data.result.email
			};
		});
	}

	profile = [
		{
			icon: 'user',
			title: 'Mi Perfil',
			route: '/profile'
		}
	];

	setting = [
		{
			icon: 'question-circle',
			title: 'Ayuda',
			route: '/parameters/list'
		}
		/*{
			icon: 'lock',
			title: 'Privacidad',
			route: '#'
		}*/
	];

	/**
	 * Verifica si una notificación tiene menos de 24 horas desde su creación
	 */
	private isWithin24Hours(createdAt: string): boolean {
		const notificationDate = new Date(createdAt);
		const now = new Date();
		const diffMs = now.getTime() - notificationDate.getTime();
		const diffHours = diffMs / (1000 * 60 * 60);
		return diffHours < 24;
	}

	/**
	 * Obtiene solo las notificaciones de las últimas 24 horas
	 */
	get recentNotifications(): IUserNotification[] {
		return this.notifications.filter((n) => this.isWithin24Hours(n.createdAt));
	}

	/**
	 * Carga las notificaciones del usuario
	 */
	loadNotifications(): void {
		this.isLoadingNotifications = true;
		this.notificationsService.getUserNotifications(1, 10).subscribe({
			next: (response) => {
				this.notifications = response.data;
				// Solo contar notificaciones no leídas que tengan menos de 24 horas
				this.unreadCount = response.data.filter((n) => !n.isRead && this.isWithin24Hours(n.createdAt)).length;
				this.isLoadingNotifications = false;
			},
			error: (error) => {
				console.error('Error al cargar notificaciones:', error);
				this.isLoadingNotifications = false;
			}
		});
	}

	/**
	 * Marca todas las notificaciones como leídas
	 */
	markAllAsRead(): void {
		this.notificationsService.markAllAsRead().subscribe({
			next: () => {
				this.notifications.forEach((n) => (n.isRead = true));
				this.unreadCount = 0;
			},
			error: (error) => {
				console.error('Error al marcar notificaciones como leídas:', error);
			}
		});
	}

	/**
	 * Obtiene el icono según el tipo de notificación
	 */
	getNotificationIcon(type: string): string {
		const iconMap: Record<string, string> = {
			info: 'bell',
			warning: 'warning',
			success: 'check-circle',
			error: 'close-circle'
		};
		return iconMap[type] || 'bell';
	}

	/**
	 * Obtiene la clase de color según el tipo de notificación
	 */
	getNotificationColorClass(type: string): string {
		const colorMap: Record<string, string> = {
			info: 'bg-light-primary',
			warning: 'bg-light-warning',
			success: 'bg-light-success',
			error: 'bg-light-danger'
		};
		return colorMap[type] || 'bg-light-primary';
	}

	/**
	 * Formatea la fecha relativa
	 */
	getRelativeTime(dateString: string): string {
		const date = new Date(dateString);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMs / 3600000);
		const diffDays = Math.floor(diffMs / 86400000);

		if (diffMins < 1) return 'Ahora mismo';
		if (diffMins < 60) return `Hace ${diffMins} min`;
		if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
		if (diffDays < 7) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
		return date.toLocaleDateString('es-ES');
	}

	/**
	 * Formatea la hora
	 */
	getFormattedTime(dateString: string): string {
		const date = new Date(dateString);
		return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
	}

	/**
	 * Inicializa el WebSocket para recibir notificaciones en tiempo real
	 */
	private initializeWebSocket(): void {
		// Conectar al WebSocket con autenticación
		this.webSocketService.connect();

		// Escuchar notificaciones (tipo: notification)
		this.notificationSubscription = this.webSocketService.listen<Record<string, unknown>>('notification').subscribe({
			next: (data) => {
				console.log('🔔 Nueva notificación recibida:', data);
				this.loadNotifications(); // Recargar notificaciones cuando llega una nueva
			},
			error: (error) => {
				console.error('Error al recibir notificación:', error);
			}
		});

		// Escuchar alertas (tipo: alert)
		this.alertSubscription = this.webSocketService.listen<Record<string, unknown>>('alert').subscribe({
			next: (data) => {
				console.warn('⚠️ Nueva alerta recibida:', data);
				this.loadNotifications(); // Recargar notificaciones cuando llega una alerta
			},
			error: (error) => {
				console.error('Error al recibir alerta:', error);
			}
		});

		// Escuchar mensajes (tipo: message)
		this.messageSubscription = this.webSocketService.listen<Record<string, unknown>>('message').subscribe({
			next: (data) => {
				console.log('💬 Nuevo mensaje recibido:', data);
				this.loadNotifications(); // Recargar notificaciones cuando llega un mensaje
			},
			error: (error) => {
				console.error('Error al recibir mensaje:', error);
			}
		});
	}
}
